import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { apis } from '@modules/apiClients'
import { trackAchievement } from '@/modules'
import { ProofOfAchievement } from '@/components/SharablePage/ProofOfAchievement'
import { AchievementBackground } from '@/components/SharablePage/AchievementBackground'
import { HiddenSharePage } from '@/components/SharablePage/HiddenSharePage'
import { ChallengeService } from '@duck/api-definitions'
import { ExternalAssets } from '@/types/externalURLs'

export const Route = createFileRoute('/submission/$slug')({
  loader: async ({ params }) => {
    try {
      const { data } = await apis.challengeService.getPublicAchievement(params.slug)
      // 'hidden' status is valid — pass it through so the component can render HiddenSharePage
      return data
    } catch (e: unknown) {
      const err = e as { response?: { status?: number } }
      // Unknown / made-up slugs return 404. Surface the same "unavailable" page
      // as a hidden achievement instead of the router's bare not-found UI.
      if (err?.response?.status === 404) {
        const notFoundPayload: ChallengeService.PublicAchievementPayload = {
          status: ChallengeService.PublicAchievementPayloadStatusEnum.NotFound,
        }
        return notFoundPayload
      }
      throw e
    }
  },
  component: SubmissionPage,
})

function SubmissionPage() {
  const { slug } = Route.useParams()
  const payload = Route.useLoaderData()

  // Hidden (owner made it private) and not-found (bad/made-up slug) both render
  // the same "unavailable" page and carry no submission detail.
  const isUnavailable =
    payload.status === ChallengeService.PublicAchievementPayloadStatusEnum.Hidden ||
    payload.status === ChallengeService.PublicAchievementPayloadStatusEnum.NotFound

  useEffect(() => {
    // Fire-and-forget view tracking
    apis.challengeService
      .recordPublicAchievementView(slug, { referrer: document.referrer || null })
      .catch(() => {})

    // Referrer host only — strip path/query to keep the property low-cardinality
    // and PII-free (matches the server-side referrerHost in achievement_views).
    let referrerHost: string | null = null
    try {
      referrerHost = document.referrer ? new URL(document.referrer).hostname : null
    } catch {
      referrerHost = null
    }

    // Mixpanel analytics
    trackAchievement('Public Achievement Viewed', {
      slug,
      taskName: payload.taskName,
      status: payload.status,
      referrerHost,
      // Unavailable payloads carry no submission detail — only emit it when public.
      grade: isUnavailable ? undefined : payload.grade,
      attemptNumber: isUnavailable ? undefined : payload.attemptNumber,
      weaponOfChoice: isUnavailable ? undefined : payload.weaponOfChoice,
    })
  }, [slug, payload, isUnavailable])

  if (isUnavailable) {
    return (
      <>
        <title>XP Lab — Achievement</title>
        <meta property="og:image" content={ExternalAssets.AchievementOGImage} />
        <meta name="twitter:image" content={ExternalAssets.AchievementOGImage} />
        <HiddenSharePage />
      </>
    )
  }

  const {
    name,
    taskName,
    attemptNumber,
    grade,
    submittedAt,
    percentile,
    workTime,
    memberSince,
    feedbackExcerpt,
    weaponOfChoice,
    organization,
  } = payload

  const pageTitle = name && taskName ? `${name} — ${taskName} | XP Lab` : 'Achievement | XP Lab'

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="robots" content="index,follow" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:type" content="profile" />
      <meta property="og:image" content={ExternalAssets.AchievementOGImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={ExternalAssets.AchievementOGImage} />
      <div className="relative min-h-screen flex items-center justify-center p-6 overflow-hidden max-sm:p-0 max-sm:items-stretch max-sm:overflow-visible">
        <AchievementBackground />

        <div className="relative z-10 max-sm:w-full">
          <ProofOfAchievement
            name={name!}
            taskName={taskName!}
            attemptNumber={attemptNumber!}
            grade={grade!}
            submittedAt={new Date(submittedAt!)}
            percentile={percentile ?? null}
            workTime={workTime ?? null}
            memberSince={memberSince ? new Date(memberSince) : null}
            feedbackExcerpt={feedbackExcerpt ?? null}
            weaponOfChoice={weaponOfChoice ?? null}
            organization={organization ?? null}
            // xs only: full-bleed card — fills the screen, no border/rounded/shadow, content vertically centered.
            className="max-sm:w-full max-sm:max-w-none max-sm:min-h-screen max-sm:rounded-none max-sm:border-0 max-sm:shadow-none max-sm:px-6 max-sm:flex max-sm:flex-col max-sm:justify-center"
          />
        </div>
      </div>
    </>
  )
}
